import type { EntityDetailResponse } from '@moment/horizon-data-access/platform'
import type { SormDataAccess } from './data-access/sorm-data-access'
import type { FieldDescriptor } from './field-descriptor'

import { queryOptions, useQuery } from '@tanstack/react-query'

import { platformSormDataAccess } from './data-access/platform-sorm-data-access'
import { sormCacheKey } from './data-access/sorm-data-access'
import { sormRequestOptions } from './data-access/sorm-request-options'
import { describeFields } from './field-descriptor'

// Schemas are managed in horizon, so they can change on a horizon deploy
// without a frontend deploy (only the latter hard-reloads the app via version
// skew detection). We still deliberately treat them as session-immutable
// (TR-3148): mid-session schema changes are rare and tolerable until the next
// reload, and never going stale kills the per-window-focus refetch fan-out.
// Unobserved entries GC after 30 minutes; explicit invalidation still
// refetches.
const SCHEMA_STALE_TIME_MS = Number.POSITIVE_INFINITY
const SCHEMA_GC_TIME_MS = 30 * 60_000

/**
 * Canonical `queryOptions` for one SORM entity's schema — every schema fetch
 * (grid column building, export field discovery, detail widgets) goes through
 * this, so they share one cache entry per entity and one caching policy.
 *
 * The query data is the unwrapped {@link EntityDetailResponse} (the
 * transport envelope is shed in the queryFn). Compose observer-level options
 * (`select`, `enabled`, …) at the call site via spread — see
 * {@link useSormEntityFields} for the descriptor-map projection.
 */
export function sormEntitySchemaQueryOptions(
  entityName: string,
  dataAccess: SormDataAccess = platformSormDataAccess,
) {
  return queryOptions({
    queryKey: sormCacheKey(dataAccess, 'sorm-schema-entity', entityName),
    queryFn: (context) =>
      dataAccess.getEntity(entityName, sormRequestOptions(dataAccess, context)),
    staleTime: SCHEMA_STALE_TIME_MS,
    gcTime: SCHEMA_GC_TIME_MS,
  })
}

/**
 * Canonical `queryOptions` for the SORM entity catalog — the list of entities
 * the runtime exposes, used to discover which entity schemas are reachable.
 * Shares the schema caching policy (session-immutable) so catalog reads never
 * refire on focus or remount.
 *
 * The query data is the unwrapped `EntityListResponse`. A `context` limits
 * the catalog to that context's entities and is part of the cache key.
 */
export function sormEntityListQueryOptions(
  dataAccess: SormDataAccess = platformSormDataAccess,
  context?: string,
) {
  return queryOptions({
    queryKey: sormCacheKey(
      dataAccess,
      'sorm-schema-entity-list',
      ...(context === undefined ? [] : [context]),
    ),
    queryFn: (queryContext) =>
      dataAccess.listEntities({
        context,
        ...sormRequestOptions(dataAccess, queryContext),
      }),
    staleTime: SCHEMA_STALE_TIME_MS,
    gcTime: SCHEMA_GC_TIME_MS,
  })
}

/**
 * Canonical `queryOptions` for the SORM context definitions (per-context
 * `query_domains` restricting which entities a surface may join). Shares the
 * schema caching policy (session-immutable).
 *
 * The query data is the unwrapped `ContextListResponse`.
 */
export function sormContextsQueryOptions(
  dataAccess: SormDataAccess = platformSormDataAccess,
) {
  return queryOptions({
    queryKey: sormCacheKey(dataAccess, 'sorm-schema-contexts'),
    queryFn: (context) =>
      dataAccess.getContexts(sormRequestOptions(dataAccess, context)),
    staleTime: SCHEMA_STALE_TIME_MS,
    gcTime: SCHEMA_GC_TIME_MS,
  })
}

/**
 * Projects an entity schema into a field-name-keyed descriptor lookup.
 * Includes fields the schema marks `visible: false` — visibility is the
 * caller's concern (see {@link FieldDescriptor.displayable}). Exported for
 * tests and non-hook call sites.
 */
export function buildFieldDescriptorMap(
  schema: EntityDetailResponse | undefined,
): ReadonlyMap<string, FieldDescriptor> {
  return new Map(
    describeFields(schema).map((descriptor) => [descriptor.name, descriptor]),
  )
}

/**
 * Field descriptors for one entity, keyed by field name — the widget-facing
 * read of {@link sormEntitySchemaQueryOptions}. The projection runs in
 * `select`, which is observer-level: imperative readers of the same cache
 * entry (`queryClient.ensureQueryData(sormEntitySchemaQueryOptions(…))`) still
 * see the raw schema.
 */
export function useSormEntityFields(entityName: string) {
  return useQuery({
    ...sormEntitySchemaQueryOptions(entityName),
    select: buildFieldDescriptorMap,
  })
}
